// janus-engine/turns/end-turn.ts — T1: End Turn on the client-truth lane (plan: plans/janus-t1-end-turn-plan-2026-09-09.md).
//
// PURE. Resolves ONE end of turn against the encounter blob the browser holds and produces the three-row commit
// (`janus.turn.end.v1`) plus the frame every browser applies: the ENDING actor's expiries, the NEXT actor's start-of-turn
// reset + expiries + overrides, the ENCOUNTER pointer / round, and any THIRD creature whose status a caster-side expiry
// removes. Mirrors the legacy request end_turn_inc.php → push_time_to_next_turn_inc.php → the end-of-turn hooks →
// ft_advance_after_main_turn_end → ft_run_start_of_main_turn_hooks, in that order (plan §1).
// Supported boundaries advance and log unavailable effects; refusals warn and resync without a legacy dispatch.

import { applyApsScalar } from '../movement/client-truth-commit.js';
import { encounterField } from '../../shared/array-types.js';
import { expirableStatuses, statusRows, statusItemId, statusPaused, type ExpiryReaders, type ExpiryResult, type StatusRemoval } from '../statuses/expiry.js';
import { applyStatusFrame, statusRemovalFrame, skillsWithoutStatuses, type EndTurnRowFrame } from './status-frame.js';
export { skillsWithoutStatus, skillsWithoutStatuses, type EndTurnRowFrame } from './status-frame.js';
import { ecLean, HandBack } from '../ability/context.js';
import { skipUnported } from '../unported.js';
import { recordingRollSource } from '../actions/roll-source.js';
import { resolveEndTurnTicks, type TurnTickReaders, type TurnTickResult } from './ticks.js';
import { normalizeOrbQueue, orbQueueText } from '../ability/orbs/orb-queue.js';
import type { ZoneEndDecision, ZoneEndPlan, ZoneEndClaim } from '../zones/end-of-turn-zones.js';
import type { TurnStartLifecycleSource, TurnStartInputSnapshot } from './input-snapshots.js';
import { statusInputSnapshot } from './input-snapshots.js';
import { legendaryPoolTotal, legendaryTurnEligible } from './k4-legendary.js';

type Row = Record<string, unknown>;

export const END_TURN_SCHEMA = 'janus.turn.end.v1' as const;
export const END_TURN_RULES_VERSION = 't1-2026-09-09';
export const PSI_ABILITY = '6A8183E1-349E-3960-B465-6C863097E193';
export const FURY_ABILITY = 'CA6567EA-74E1-41CC-9C7C-E8EEA8C0B6D6';
export const MASTERY_POOL_ABILITY = 'B56929A7-6BB8-81FA-BFF8-2CE4B0812030';
export const MELODY_MECHANIC = 'BD233002-F6C0-A136-6EE4-C899A9E92856';
/** The lean row builder's floor for a creature's maximum AP (reset_encounter_creature_start_of_turn_variables). */
const MIN_APS_TOTAL = 3;
const MAX_APS = 127;
const ROUND_SECONDS = 6;
export const REFUSALS = {
  notInCombat: 'You can only End Turn while the encounter is in combat!',
  noPointer: 'current_encounter_creature_id_turn is empty, no creatures in encounter to end turn?',
  notYourTurn: 'It is not your turn to End Turn!',
} as const;

export interface AbilityEntry {
  id: string;
  type: string;
  definition: Row | null;
  charges: number | null;
  max: number | null;
  /** passive_conditions_active === false on a compiled passive (kept for display, inert). */
  inactive: boolean;
}
export interface EndTurnReaders extends ExpiryReaders {
  /** Shared ability readers and the live roll source for turn ticks. */
  tick?: TurnTickReaders;
  prepareTick?: (result: TurnTickResult) => void;
  startSource?: (status: Row, ending: Row) => TurnStartLifecycleSource | null;
  /** Every roster row of the encounter (encounter_creatures_arr). */
  roster: () => Row[];
  /** Per-creature combat membership (creatureIsInActiveCombat twin). */
  inCombat: (row: Row) => boolean;
  /** Every ability entry the row carries, across the AbilityType buckets, with its resolved definition (null = unheld). */
  abilityEntries: (row: Row) => AbilityEntry[];
  /** Does the row carry a `triggers_when_caster_turn_begins` carrier (any carrier bucket)? */
  carriesTurnBeginTrigger: (row: Row) => boolean;
  /** computed.creature.skills bucket KEY for a skill item id (SKILL_CATALOG). */
  skillVariable: (skillId: string) => string | null;
  /** Fresh uppercase UUID (operation_id). */
  uuid: () => string;
  /** Page zone plan or named unavailable effect; persisted dedupe facts are preflighted before real draws. */
  zoneEndOfTurn?: (endingId: string) => ZoneEndDecision;
}
export interface EndTurnInput {
  encounterId: string;
  /** The creature whose turn ends: the pointer actor (the dock's End Turn, or the GM's next-turn wrapper). */
  actorId: string;
  userId: string;
  isGM: boolean;
  /** Staff tier (employee / admin / super_admin): the server grants staff player permission on every creature
   * (get_encounter_creature_permissions → is_at_least_employee), so the twin must not refuse them locally. */
  isStaff?: boolean;
  rollPrivate?: number;
}
export interface EndTurnNextFrame extends EndTurnRowFrame {
  /** False for an action window: retain every resource and armed watch. */
  refresh?: boolean;
  aps_total?: number;
  aps: number; aps_used: number | null; weapon_attack: 0; head_attack: 0; reaction: 0 | 1; movement_pct: 0;
}
export interface EndTurnFrame {
  /** GM-only skipped effects, posted through tick.rows on the existing commit. */
  skipped?: ReturnType<typeof skipUnported>[];
  ending: EndTurnRowFrame;
  next: EndTurnNextFrame;
  others: Array<{ actor_id: string } & EndTurnRowFrame>;
  encounter: { current_encounter_creature_id_turn: string; current_round: number; turn_phase: 'main' | 'legendary'; pending_main_turn_encounter_creature_id?: string; legendary_turn_queue?: string };
  clock_seconds: 0 | 6;
}
export interface ApChangeSource { kind: 'status' | 'status_added' | 'passive' | 'orb'; id: string; delta: number }
export interface EndTurnCommand {
  schema: typeof END_TURN_SCHEMA;
  operation_id: string;
  encounter_id: string;
  actor_id: string;
  expected_rev: number;
  rules_version: typeof END_TURN_RULES_VERSION;
  next_id: string;
  next_expected_rev: number;
  next_orb_queue_before: string | null;
  ending_orb_queue_before?: string | null;
  zone_dedupe_claims?: ZoneEndClaim[];
  /** Identities of transient END additions read by the START reset, even if a later START evoke retires them. */
  start_status_sources?: Array<{ status_item_id: string; target: string; caster: string; status_connected_id: string; source?: TurnStartLifecycleSource }>;
  start_input_snapshot?: TurnStartInputSnapshot;
  wrap: boolean;
  roll_private: number;
  before: { round: number; pending_main_turn_encounter_creature_id: string; legendary_turn_queue: string;
    turn_phase?: string;
    next: { aps_total: number; encounter_creature_dead: number; aps: number; weapon_attack: number; head_attack: number; reaction: number; movement_pct: number } };
  tick_before_ec?: Record<string, Record<string, number | string | null>>;
  writes: {
    ending: { status_remove: StatusRemoval[] };
    next: { refresh?: boolean; aps_total?: number; aps: number; weapon_attack: 0; head_attack: 0; reaction: 0 | 1; movement_pct: 0; status_remove: StatusRemoval[] };
    others: Array<{ actor_id: string; status_remove: StatusRemoval[] }>;
    encounter: { current_encounter_creature_id_turn: string; current_round?: number; turn_phase?: 'main' | 'legendary'; pending_main_turn_encounter_creature_id?: string; legendary_turn_queue?: string };
    ap_change_sources: ApChangeSource[];
    log: ['end_turn', 'start_turn' | 'start_legendary_turn'];
  };
  frame: EndTurnFrame;

  /** Raw zone anchors and status inputs captured by the page. */
  zones_checked?: string[];
  zone_inputs?: Row[];
  /** Ordered PHP roll rows and tick mutations under the End Turn roll id (oracle lane). */
  tick?: TurnTickResult;
  rev?: number;
}
export type EndTurnScope =
  | { supported: true; command: EndTurnCommand; ending: Row; next: Row; others: Row[] }
  | { supported: false; refused: true; message: string }
  | { supported: false; refused: false; reason: string; reasons?: string[] };

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const present = (value: unknown): boolean => value !== undefined && value !== null && value !== '';
const int = (value: unknown, fallback = 0): number => { const n = Number(scalar(value)); return Number.isFinite(n) ? Math.trunc(n) : fallback; };
const ecOf = (row: Row): Row => record(row.encounter_creatures) ?? {};
const idOf = (row: Row): string => upper(ecOf(row).encounter_creature_id);
const itemType = (row: Row): string => upper(scalar(ecOf(row).item_type) ?? scalar(record(row.items)?.item_type) ?? row.item_type ?? '');

function legendaryEligible(row: Row, read: EndTurnReaders): boolean {
  return legendaryTurnEligible(row, read);
}

function mainTurnTotal(row: Row, read: EndTurnReaders): number {
  const pool = legendaryPoolTotal(row, read);
  if (pool !== null) return Math.min(MAX_APS, pool);
  return int(ecLean(row, 'aps_total'), MIN_APS_TOTAL);
}

/** build_initiative_encounter_creature_id_arr twin over the rows the browser holds: living, in the open combat, public
 * (a LAIR while private), a PC / creature / creature_model / lair or a numbered row (the SQL's real-combatant predicate),
 * initiative DESC. Ties are reported (SQL has no tiebreak). */
export function initiativeOrder(encounter: Row, read: Pick<EndTurnReaders, 'roster' | 'inCombat'>): { order: string[]; tie: boolean } {
  const members = read.roster().filter(row => {
    const ec = ecOf(row);
    if (int(ec.encounter_creature_dead) !== 0 || int(ec.encounter_creature_deleted) !== 0) return false;
    if (int(ec.encounter_creature_private) !== 0 && itemType(row) !== 'LAIR') return false;
    if (!read.inCombat(row)) return false;
    return ['PLAYER_CHARACTER', 'CREATURE', 'CREATURE_MODEL', 'LAIR'].includes(itemType(row)) || int(ec.creature_number) > 0;
  });
  const initiative = (row: Row): number => Number(scalar(ecOf(row).creature_initiative) ?? 0);
  const sorted = [...members].sort((a, b) => initiative(b) - initiative(a));
  const tie = sorted.some((row, i) => i > 0 && initiative(row) === initiative(sorted[i - 1]!));
  void encounter;
  return { order: sorted.map(idOf), tie };
}

/** Σ ap_change_at_start_of_turn over the next actor's ACTIVE target statuses (minus the ids an earlier pass removed —
 * legacy's reset reads the arrays after the END hooks mutated them) and its active passives. */
function sumApChange(next: Row, read: EndTurnReaders, removed: ReadonlySet<string>): { total: number; sources: ApChangeSource[]; missing: string | null } {
  const sources: ApChangeSource[] = [];
  let total = 0;
  let missing: string | null = null;
  for (const status of statusRows(next, 'target')) {
    if (statusPaused(status) || removed.has(upper(scalar(status.status_id)))) continue;
    const nested = record(status.status_arr);
    const def = nested && record(nested.item_layer_types) ? nested : read.definition(statusItemId(status));
    if (!def) { missing ??= statusItemId(status); continue; }
    const delta = read.value(def, 'ap_change_at_start_of_turn');
    if (!present(delta)) continue;
    total += int(delta); sources.push({ kind: status.tick_added ? 'status_added' : 'status', id: status.tick_added ? statusItemId(status) : upper(scalar(status.status_id)), delta: int(delta) });
  }
  for (const entry of read.abilityEntries(next)) {
    if (entry.type !== 'passive_ability' || entry.inactive) continue;
    if (!entry.definition) { missing ??= entry.id; continue; }
    // ft_passive_ap_change_at_start_of_turn_total() SKIPS a passive that is itself a combat-begins carrier (Hot Start): its live status owns
    // the AP bonus and expiry — counting the authored passive too doubled the grant every turn (lane A4, for N5b, 2026-10-01; PHP !empty()).
    const begins = read.value(entry.definition, 'triggers_after_combat_begins');
    if (present(begins) && begins !== 0 && begins !== '0' && begins !== false && !(Array.isArray(begins) && begins.length === 0)) continue; // PHP !empty()
    const delta = read.value(entry.definition, 'ap_change_at_start_of_turn');
    if (!present(delta)) continue;
    total += int(delta); sources.push({ kind: 'passive', id: entry.id, delta: int(delta) });
  }
  // ft_orb_fire_ap_bonus(): +1 AP per channeled FIRE orb (StS Plasma's passive — read straight off the queue, no authored item; lane A10, R-026
  // plan F2 — the next actor's non-empty queue was a blanket `orb-queue` hand-back). One source carrying the fire orb's OrbType id.
  const fire = (normalizeOrbQueue(ecOf(next).orb_queue) ?? []).filter(slug => slug === 'fire').length;
  if (fire > 0) { total += fire; sources.push({ kind: 'orb', id: FIRE_ORB_ID, delta: fire }); }
  return { total, sources, missing };
}
/** The gates the turn TICK lane serves when its readers are present (ticks.ts): dice / charge / regeneration / turn-begin / orb-pulse work. */
const TICK_GATES: ReadonlySet<string> = new Set(['dot-tick', 'contest-retry', 'ability-recharge-pending', 'ability-decay-pending', 'psi-regen-pending',
  'turn-begin-trigger-carrier', 'regeneration-source', 'orb-queue-end', 'orb-channel-each-turn', 'mastery-pool',
  // R-176: a damage_on_status_expiry row expiring at the ending actor's END (ticks.ts endTargetTicks → status-expiry-detonation.ts)
  'expiry-detonation', 'maintained-expiry']);
/** OrbType 'fire' (includes/additions.php) — the AP source id the turn commit binds to the next actor's stored queue. */
export const FIRE_ORB_ID = '1F4F3172-2CE9-4DC2-82B0-2280155608FD';

const CONCENTRATING_STATUS = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
/** Timed-activity statuses (StatusEffect casting, casting_ritual_spell, gathering, lockpicking, reading, training, donning,
 *  doffing, disenchanting). Legacy's start-of-turn completes a due one (timed_activity_completion.php, 2026-09-16) — a janus()
 *  finish this lane cannot run — so a next actor holding one is a gate; the endpoint re-derives it from the locked rows. */
export const TIMED_ACTIVITY_STATUSES: readonly string[] = [
  '5D939B6E-B9BC-7AC5-5143-4F00F4189EA3', '5833A733-D277-0A60-3A94-A0F355A79A80', '3F171782-CC56-32DF-81B4-8C0ED89F47F4',
  'FF9A38A3-5F35-F083-E153-7DF41B0C0826', 'ED30CCB0-1645-B97E-B9A6-867D8D0094BD', '426C7E0B-EF44-498C-10B2-B50A9E28E45C',
  'D343892B-338C-4FBE-BEA0-2015FE503F43', '398DD6E8-AFAE-EA0D-6FE9-C22842D53775', '1C4D81BB-FB79-68AD-E881-28D874927FD2',
];
/** The same ids → the StatusEffect variable_name (the finish family a due one hands back under). */
export const TIMED_ACTIVITY_FAMILY: Readonly<Record<string, string>> = Object.fromEntries(TIMED_ACTIVITY_STATUSES.map((id, i) =>
  [id, ['casting', 'casting_ritual_spell', 'gathering', 'lockpicking', 'reading', 'training', 'donning', 'doffing', 'disenchanting'][i]!]));

/** R-117 export (Janus lane N1b for lane N5b, 2026-10-01) — the START-OF-TURN reset + hooks of ONE actor, factored out of
 *  resolveEndTurn (which now calls it for its NEXT actor, unchanged: byte-identical over the turn-tick census corpus). The combat START
 *  needs it per member (apply_initiative_to_ids → reset_encounter_creature_start_of_turn_variables) and for the first combatant's
 *  start-of-turn block (ft_run_start_of_main_turn_hooks). PURE: reads the row + the readers, writes nothing.
 *  Mirrors legacy's order: the lair/legendary pool, the aps_total floor, a pending recharge / decay / psi regen /
 *  regeneration source, a turn-begin trigger carrier, the orb queue / channel), the reset's
 *  ap_change_at_start_of_turn sum (statuses minus `removedBefore`, active passives), then the START expiries at the advanced clock
 *  and their no_actions / no_reactions overrides. Every gate is STATE only (decided before any mutation): a non-empty `gates` means
 *  a due hook this resolver does not run — the boundary caller records a skip. */
export interface StartOfTurnOptions {
  refreshPool?: boolean;
  /** Status ids an earlier pass already removed (End Turn: the ending actor's END expiries) — the reset and the start passes skip them. */
  removedBefore?: ReadonlySet<string>;
  /** Seconds the campaign clock advances before the start passes run (End Turn: the wrap's +6 s; a combat start: 0). */
  clockAdvanceSeconds?: number;
}
export interface StartOfTurnResult {
  actor_id: string;
  /** Hand-back reasons in the legacy hook order, deduplicated. Empty = every start-of-turn hook is decided here. */
  gates: string[];
  /** The row's stored aps_total (floor MIN_APS_TOTAL when absent) the reset adds the ap_change sum to. */
  stored_total: number;
  ap_change_sources: ApChangeSource[];
  /** reset_encounter_creature_start_of_turn_variables: the values the row takes (aps_used = stored_total − aps when > 0, else null). */
  reset: { aps: number; aps_used: number | null; weapon_attack: 0; head_attack: 0; reaction: 0 | 1; movement_pct: 0 };
  /** reset_encounter_creature_start_of_turn_variables ALONE — aps = clamp(stored_total + Σ ap_change), reaction 1, no start-pass
   *  overrides: the per-member reset a combat START applies (apply_initiative_to_ids) before any start-of-turn hook runs. `reset` is that
   *  value after the START pass (a no_actions / no_reactions status left standing) — what the turn holder's start-of-turn block writes. */
  bare: { aps: number; reaction: 1 };
  /** The row's values before the reset (the commit's CAS / audit). */
  before: { aps_total: number; aps: number; weapon_attack: number; head_attack: number; reaction: number; movement_pct: number };
  /** The START expiries (caster-start / target-start / calendar-start) the hooks remove — as expirableStatuses returns them (it already
   *  skips `removedBefore`). A removal may live on a THIRD row (a caster-start family): its target_id says which. */
  removals: StatusRemoval[];
  /** The start pass's overrides the reset read (a no_actions / no_reactions status left after the start expiries). */
  overrides: { no_actions: boolean; no_reactions: boolean };
  /** Page-side peer closures forwarded to End Turn's projection filter. */
  expiryGroups?: string[][];
  /** Lane A10: every live (unpaused) timed-activity row the actor holds that this boundary leaves alone (not due, or its owner blocked) —
   *  the End Turn command lists them so the commit can bind the plan to its locked rows. A row that would complete is a gate instead. */
}
export function resolveStartOfTurn(row: Row, read: EndTurnReaders, opts: StartOfTurnOptions = {}): StartOfTurnResult {
  const gates: string[] = [];
  const gate = (reason: string): void => { if (!gates.includes(reason)) gates.push(reason); };
  const removedBefore = opts.removedBefore ?? new Set<string>();
  const actorId = idOf(row);
  const ec = ecOf(row);
  const storedTotal = opts.refreshPool === false ? int(ecLean(row, 'aps_total')) : mainTurnTotal(row, read);
  if (opts.refreshPool === false && storedTotal !== mainTurnTotal(row, read)) gate('main-turn-pool-refresh');
  if (storedTotal < MIN_APS_TOTAL && itemType(row) !== 'LAIR') gate('aps-total-below-minimum');
  const vitality = read.vitality(row);
  const concentrating = statusRows(row, 'target').some(s => statusItemId(s) === CONCENTRATING_STATUS);
  for (const entry of read.abilityEntries(row)) {
    if (entry.type === 'lair_ability') continue;
    if (!entry.definition) { gate('definition-missing:' + entry.id); continue; }
    const def = entry.definition;
    if (present(read.value(def, 'recharge_range_end')) && entry.charges !== null && entry.max !== null && entry.charges < entry.max) gate('ability-recharge-pending');
    if (present(read.value(def, 'ability_charge_decay_at_start_of_turn')) && (entry.charges ?? 0) > 0 && !(entry.id === FURY_ABILITY && present(read.value(row, 'controlled_fury')))) gate('ability-decay-pending');
    if (entry.id === PSI_ABILITY && !concentrating && entry.charges !== null && entry.max !== null && entry.charges < entry.max) gate('psi-regen-pending');
    if (upper(read.value(def, 'roll_type')) === '9909BE44-99AE-8780-BB12-BD99BA6E672E' && vitality.current < vitality.max) gate('regeneration-source');
  }
  if (read.carriesTurnBeginTrigger(row)) gate('turn-begin-trigger-carrier');
  // The next actor's ORBS (lane A10, R-026 plan F2): a non-empty queue alone does nothing at a turn start but the fire orbs' +1 AP (summed
  // into the reset below — sumApChange). ft_orb_start_of_turn_channel_pass replays a channel_orb_each_turn applier's channel block (a force
  // evoke past the cap). The tick lane serves it; callers without tick readers retain the hook gate.
  if (statusRows(row, 'target').some(s => { const d = record(s.status_arr) ?? read.definition(statusItemId(s)); return !statusPaused(s) && d && int(read.value(d, 'channel_orb_each_turn')) === 1; })) gate('orb-channel-each-turn');
  // The reset reads the statuses as they stand AFTER any earlier pass (an END removal on this actor is gone) and BEFORE the start passes
  // (a caster-START row still counts) — legacy's exact order (Codex A review F2).
  const apChange = sumApChange(row, read, removedBefore);
  if (apChange.missing) gate('definition-missing:' + apChange.missing);
  const startRead: EndTurnReaders = { ...read, nowSeconds: read.nowSeconds + (opts.clockAdvanceSeconds ?? 0),
    ...(read.tick ? { carriesLossTrigger: () => false } : {}) };
  const startExpiry = expirableStatuses([row], { kind: 'start', actorId }, startRead, removedBefore);
  startExpiry.gates.forEach(gate);
  const apsAfter = Math.max(0, Math.min(MAX_APS, storedTotal + apChange.total));
  const aps = startExpiry.overrides.no_actions ? 0 : apsAfter;
  const reaction: 0 | 1 = startExpiry.overrides.no_reactions ? 0 : 1;
  const apsUsed = Math.max(0, storedTotal - aps);
  const result: StartOfTurnResult = {
    actor_id: actorId, gates, stored_total: storedTotal, ap_change_sources: apChange.sources,
    reset: { aps, aps_used: apsUsed > 0 ? apsUsed : null, weapon_attack: 0, head_attack: 0, reaction, movement_pct: 0 },
    bare: { aps: apsAfter, reaction: 1 },
    before: { aps_total: storedTotal, aps: int(ec.aps), weapon_attack: int(ec.weapon_attack), head_attack: int(ec.head_attack), reaction: int(ec.reaction), movement_pct: Number(scalar(ec.movement_pct) ?? 0) },
    removals: startExpiry.removals, overrides: { no_actions: startExpiry.overrides.no_actions, no_reactions: startExpiry.overrides.no_reactions },
  };
  if (startExpiry.groups) Object.defineProperty(result, 'expiryGroups', { value: startExpiry.groups });
  return result;
}

/** Janus Session A2 step 4c (2026-10-01): the boundary when the turn holder DIES mid-turn — kill_creature → advance_turn_if_dying_creatures_turn
 *  → ft_advance_after_main_turn_end with the PRE-KILL initiative. The SAME resolver in `death` mode (reuse, never a fork): no permission (a
 *  death ends the turn whoever dealt it), no END hooks and no end_turn log for the dead (legacy runs none); everything on the NEXT side is
 *  unchanged — its gates (legendary interlude, enrol, recharge, the 0-AP auto-end loop …) stay hand-backs. */
export interface TurnDeathTransition {
  dying_id: string; next_id: string; next_expected_rev: number; wrap: boolean; round_before: number;
  start_log?: 'start_turn' | 'start_legendary_turn';
  phase_before?: string;
  before_next: EndTurnCommand['before']['next'];
  next: EndTurnCommand['writes']['next'];
  others: EndTurnCommand['writes']['others'];
  encounter: EndTurnCommand['writes']['encounter'];
  ap_change_sources: ApChangeSource[];
  frame: EndTurnFrame;
}
export type TurnDeathResult =
  | { kind: 'transition'; transition: TurnDeathTransition }
  /** legacy advances nothing: not in combat, not the pointer, or fewer than two combatants in the pre-kill order (build_initiative < 2) */
  | { kind: 'none'; why: string }
  | { kind: 'handback'; reason: string };
export function resolveTurnDeath(input: { encounterId: string; dyingId: string }, encounter: Row, read: EndTurnReaders): TurnDeathResult {
  if (int(encounterField(encounter, 'in_combat')) !== 1) return { kind: 'none', why: 'not-in-combat' };
  const pointer = upper(encounterField(encounter, 'current_encounter_creature_id_turn'));
  if (!pointer || pointer !== upper(input.dyingId)) return { kind: 'none', why: 'not-turn-holder' };
  // the tick lane (recharge / decay / psi / turn-begin carriers) is the End Turn commit's — in death mode those stay hand-backs
  const { tick: _tick, ...plain } = read; void _tick;
  const scope = resolveEndTurn({ encounterId: input.encounterId, actorId: input.dyingId, userId: '', isGM: true }, encounter, plain, 'death');
  if (scope.supported) {
    const c = scope.command;
    // a START expiry the next actor's hooks make on the DYING row (its caster-side start family) still happens — the row stays: it rides as
    // a third party, never as an "ending" write (death mode writes nothing for the ending actor)
    const onDying = c.writes.ending.status_remove;
    const others = onDying.length ? [...c.writes.others, { actor_id: c.actor_id, status_remove: onDying }] : c.writes.others;
    const frame = onDying.length ? { ...c.frame, ending: { status_remove_ids: [], skills: {} }, others: [...c.frame.others, { actor_id: c.actor_id, ...c.frame.ending }] } : c.frame;
    return { kind: 'transition', transition: { dying_id: c.actor_id, next_id: c.next_id, next_expected_rev: c.next_expected_rev, wrap: c.wrap,
      round_before: c.before.round, before_next: c.before.next, next: c.writes.next, others, encounter: c.writes.encounter,
      ap_change_sources: c.writes.ap_change_sources, start_log: c.writes.log[1], phase_before: c.before.turn_phase ?? 'main', frame } };
  }
  if (scope.refused) return { kind: 'handback', reason: 'turn-death:refused' };
  return scope.reason === 'death-fewer-than-two' ? { kind: 'none', why: scope.reason } : { kind: 'handback', reason: 'turn-death:' + scope.reason };
}

export function resolveEndTurn(input: EndTurnInput, encounter: Row, read: EndTurnReaders, mode: 'end' | 'death' = 'end'): EndTurnScope {
  read = { ...read, immunityExpiry: true };
  const death = mode === 'death';
  const no = (reason: string, reasons?: string[]): EndTurnScope => ({ supported: false, refused: false, reason, reasons });
  const refuse = (message: string): EndTurnScope => ({ supported: false, refused: true, message });
  const gates: string[] = [];
  const gate = (reason: string): void => { if (!gates.includes(reason)) gates.push(reason); };

  // An expiry we cannot project stays present for every later hook and AP reset.
  // Decide this before the tick lane consumes its removal set.
  const acceptedExpiries = (candidates: StatusRemoval[], prior: StatusRemoval[] = [], groups: string[][] = []): StatusRemoval[] => {
    const blocked = new Set<string>();
    for (const r of candidates) if ((!read.tick && !read.rowFor(r.target_id)) || (r.caster_id && !read.rowFor(r.caster_id))) {
      gate('third-party-row-missing'); blocked.add(r.status_id);
    }
    // Dropping a group changes the skill reversal on its other targets. Recheck until the retained set is stable.
    for (;;) {
      const before = blocked.size;
      for (const group of groups) if (group.some(id => blocked.has(id))) {
        gate('status-connected-peers'); group.forEach(id => blocked.add(id));
      }
      const held = candidates.filter(r => !blocked.has(r.status_id));
      for (const id of new Set(held.map(r => r.target_id))) {
        const ids = [...prior, ...held].filter(r => r.target_id === id).map(r => r.status_id);
        const target = read.rowFor(id);
        if (target && skillsWithoutStatuses(target, ids).netZeroWithLines.length) {
          gate('status-skill-net-zero'); held.filter(r => r.target_id === id).forEach(r => blocked.add(r.status_id));
        }
      }
      if (blocked.size === before) return held;
    }
  };
  // ---- the legacy gates, in their order (end_turn_inc.php:14–111, push_time_to_next_turn_inc.php:39) ----
  if (int(encounterField(encounter, 'in_combat')) !== 1) return refuse(REFUSALS.notInCombat);
  const pointer = upper(encounterField(encounter, 'current_encounter_creature_id_turn'));
  if (!pointer) return refuse(REFUSALS.noPointer);
  const legendary = upper(encounterField(encounter, 'turn_phase') ?? 'main') === 'LEGENDARY';
  const actorId = upper(input.actorId);
  if (actorId !== pointer) {
    if (!input.isGM) return refuse(REFUSALS.notYourTurn);
    return no('gm-actor-not-pointer');
  }
  const ending = read.rowFor(actorId);
  if (!ending) return no('actor-row');
  // Who may end this turn (owner 2026-09-27): the GM, the creature's own player (its creator or `permissioned` user), or —
  // for a summon / companion — its CONTROLLER's player. The server twin is get_encounter_creature_permissions() behind
  // ft_viewer_may_end_current_turn(); anyone else is refused HERE with the server's own words (no round trip).
  const viewer = upper(input.userId);
  const plays = (row: Row): boolean => viewer !== '' && (upper(ecOf(row).encounter_creature_created_by_id) === viewer || upper(read.value(row, 'permissioned')) === viewer);
  const controllerId = upper(ecOf(ending).controller_encounter_creature_id);
  const controller = controllerId ? read.rowFor(controllerId) : null;
  // death mode: no permission — the death ends the turn whoever dealt it (kill_creature advances unconditionally)
  if (!death && !input.isGM && !input.isStaff && !plays(ending) && !(controller && plays(controller))) return refuse(REFUSALS.notYourTurn);

  // ---- the walk (push_time_to_next_turn_inc.php; an ended actor missing from the order restarts at the top, as legacy) ----
  const { order, tie } = initiativeOrder(encounter, read);
  if (order.length === 0) return no('initiative-empty');
  // advance_turn_if_dying_creatures_turn: "Nobody else to advance to" — the pointer stays (the queued combat-exit check ends the fight)
  if (death && !legendary && order.length < 2) return no('death-fewer-than-two');
  if (tie) gate('initiative-tie');
  const endedKey = order.indexOf(actorId);
  let wrap = !legendary && endedKey !== -1 && actorId === order[order.length - 1];
  const pending = upper(encounterField(encounter, 'pending_main_turn_encounter_creature_id'));
  const mainId = (legendary ? (order.includes(pending) ? pending : order[0])
    : (wrap || endedKey === -1) ? order[0] : order[endedKey + 1]) as string;
  let queue: string[] = [];
  if (legendary) {
    const raw = String(encounterField(encounter, 'legendary_turn_queue') ?? '');
    try {
      const decoded: unknown = raw.trim() ? JSON.parse(raw) : [];
      if (Array.isArray(decoded)) queue = decoded.filter((id): id is string => typeof id === 'string'
        && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id)).map(upper);
      else gate('legendary-queue-malformed');
    } catch { gate('legendary-queue-malformed'); }
  } else {
    queue = order.filter(id => id !== actorId && legendaryEligible(read.rowFor(id)!, read));
    if (death && queue.length) { gate('legendary-eligible'); queue = []; }
  }
  let windowId: string | undefined;
  while (queue.length) {
    const id = queue.shift()!;
    const row = read.rowFor(id);
    if (row && legendaryEligible(row, read)) { windowId = id; break; }
  }
  const window = !!windowId;
  let nextId = windowId ?? mainId;
  const interlude = legendary || window ? { turn_phase: window ? 'legendary' as const : 'main' as const,
    pending_main_turn_encounter_creature_id: window ? (legendary ? pending : mainId) : '',
    legendary_turn_queue: window ? JSON.stringify(queue) : '' } : {};
  const initialNext = read.rowFor(nextId);
  if (!initialNext) return no('next-row');
  let next: Row = initialNext;

  // ---- roster-wide gates (zones and the wrap's enrol sweep) ----
  const roster = read.roster();
  // Fixed walls live on the encounter, and carried zones can live on inventory.
  // Neither appears in the creature roster. Skipping these anchors would commit a
  // turn without running PHP's occupancy/damage hooks (painted-wall smoke, 2026-09-22).
  const zoneHolders = [encounter, ...roster,
    ...(Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr.map(record).filter((r): r is Row => r !== null) : [])];
  // collect_persistent_aoes reads zone anchors (status_aoe_source = 1), not every occupant's copied status definition — the SAME set the turn
  // commit locks (its zone guard)
  const anchorIds = new Set<string>();
  // each anchor's FINGERPRINT (A11 Codex review Q4: an id alone does not bind a zone that moved / grew / unpaused, or an aura whose carrier moved,
  // between the judgement and the commit): its span, type, pause state and — a creature-carried aura — its carrier's position, 3 decimals
  const fingerprints = new Map<string, string>();
  const f3 = (v: unknown): string => { const n = Number(scalar(v) ?? 0); return (Number.isFinite(n) ? n : 0).toFixed(3); };
  for (const holder of zoneHolders)
    for (const status of statusRows(holder, 'target')) {
      if (int(status.status_aoe_source) === 0) continue;
      const id = upper(status.status_id);
      anchorIds.add(id);
      const carrier = holder !== encounter && record(holder.encounter_creatures) ? holder : null;
      const at = carrier ? (record(carrier.position) ?? ecOf(carrier)) : null;
      fingerprints.set(id, [id, f3(status.status_origin_point_x), f3(status.status_origin_point_y), f3(status.status_origin_point_z),
        f3(status.status_distance_point_x), f3(status.status_distance_point_y), f3(status.status_distance_point_z), f3(status.status_aoe_distance),
        upper(status.status_aoe_type), statusPaused(status) ? '1' : '0', at ? `${f3(at.x_pos)},${f3(at.y_pos)},${f3(at.z_pos)}` : ''].join('|'));
    }
  // Unavailable zone work is skipped; legendary windows have no main-turn END zone hooks.
  let zonesChecked: string[] | undefined;
  let zonePlan: ZoneEndPlan | undefined;
  const decided = !death && !legendary && read.zoneEndOfTurn ? read.zoneEndOfTurn(actorId) : null;
  if (!legendary && (anchorIds.size || decided?.kind === 'handback')) {
    if ((decided?.kind === 'clear' || (decided?.kind === 'plan' && read.tick)) && [...anchorIds].every(id => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id))) {
      zonesChecked = [...anchorIds].sort().map(id => fingerprints.get(id)!);
      if (decided.kind === 'plan') zonePlan = decided;
    }
    else gate(decided?.kind === 'handback' ? `persistent-aoe-present:${decided.reason}` : 'persistent-aoe-present');
  }
  for (const row of roster) {
    const ec = ecOf(row);
    // The enrol sweep only ever ADDS uncontrolled, public, living non-lairs that are not in the combat; a controlled row
    // joins with its controller and is never a candidate of its own (ft_combat_grow_participants).
    if (wrap && int(ec.encounter_creature_dead) === 0 && int(ec.encounter_creature_private) === 0 && itemType(row) !== 'LAIR' && !present(ec.controller_encounter_creature_id) && !read.inCombat(row)) gate('enrol-candidates');
  }

  // ---- the ENDING actor: end-of-turn hooks (plan §1.4) — the ORIGINAL clock. Death mode: none (the dead run no end-of-turn hooks) ----
  if (!death && !legendary) {
    if (itemType(ending) === 'LAIR' && !read.tick && read.abilityEntries(ending).some(a => a.max !== null && a.charges !== a.max)) gate('lair-charge-refresh');
    const endingAbilities = read.abilityEntries(ending);
    if (endingAbilities.some(a => a.id === MASTERY_POOL_ABILITY && (a.charges ?? 0) > 0) || statusRows(ending, 'target').some(s => statusItemId(s) === 'EEDD357F-C1CA-44C1-A53F-DD06EEFBA597')) gate('mastery-pool');
    if (endingAbilities.some(a => a.id === MELODY_MECHANIC && (a.max ?? 0) > 0)) gate('melody-mechanic');
    // the ENDING actor's channeled orbs pulse (end_turn_inc.php ft_orb_end_of_turn_pass) — the tick lane runs that pass (ticks.ts
    // orbEndOfTurnPass, lane A10, R-026 plan F2), so this is a TICK gate (served when the tick readers are present). A JSON LIST column, or
    // its column text on a row a lane wrote: never scalar()-unwrapped.
    if ((normalizeOrbQueue(ecOf(ending).orb_queue) ?? []).length > 0) gate('orb-queue-end');
  }
  const endExpiry: ExpiryResult = death || legendary ? { removals: [], gates: [], overrides: { no_actions: false, no_reactions: false } } : expirableStatuses([ending], { kind: 'end', actorId },
    { ...read, immunityExpiry: true, ...(read.tick ? { carriesLossTrigger: () => false } : {}) });
  endExpiry.gates.forEach(gate);
  endExpiry.removals = acceptedExpiries(endExpiry.removals, [], endExpiry.groups);
  const removedAtEnd = new Set(endExpiry.removals.map(r => r.status_id));
  const acceptedStart = (row: Row, block: StartOfTurnResult): StartOfTurnResult => {
    const original = block.removals;
    block.removals = acceptedExpiries(original, endExpiry.removals, block.expiryGroups);
    const retained = new Set(original.filter(r => !block.removals.includes(r)).map(r => r.status_id));
    // A skipped START expiry remains active, including its action/reaction locks.
    for (const status of statusRows(row, 'target')) {
      if (!retained.has(upper(scalar(status.status_id)))) continue;
      const def = record(status.status_arr) ?? read.definition(statusItemId(status));
      if (!def) continue;
      if (int(read.value(def, 'no_actions'))) { block.reset.aps = 0; block.overrides.no_actions = true; }
      if (int(read.value(def, 'no_reactions'))) { block.reset.reaction = 0; block.overrides.no_reactions = true; }
    }
    const used = Math.max(0, block.stored_total - block.reset.aps);
    block.reset.aps_used = used || null;
    return block;
  };

  // ---- the NEXT actor: start-of-turn hooks (plan §1.6) — AFTER the wrap's +6 s clock push (Codex A review F1). The standalone R-117
  // resolver (resolveStartOfTurn above) — the same gates in the same order, the reset, the START expiries. ----
  let nextEc = ecOf(next);
  const windowStart = (): StartOfTurnResult => ({ actor_id: nextId, gates: [], stored_total: int(ecLean(next, 'aps_total')),
    ap_change_sources: [], reset: { aps: int(ecLean(next, 'aps')), aps_used: int(ecLean(next, 'aps_used')) || null,
      weapon_attack: 0, head_attack: 0, reaction: int(ecLean(next, 'reaction')) ? 1 : 0, movement_pct: 0 },
    bare: { aps: int(ecLean(next, 'aps')), reaction: 1 }, before: { aps_total: int(ecLean(next, 'aps_total')), aps: int(ecLean(next, 'aps')),
      weapon_attack: int(ecLean(next, 'weapon_attack')), head_attack: int(ecLean(next, 'head_attack')), reaction: int(ecLean(next, 'reaction')),
      movement_pct: Number(scalar(ecLean(next, 'movement_pct')) ?? 0) }, removals: [], overrides: { no_actions: false, no_reactions: false } });
  let start = window ? windowStart() : acceptedStart(next, resolveStartOfTurn(next, read, { refreshPool: !death, removedBefore: removedAtEnd, clockAdvanceSeconds: wrap ? ROUND_SECONDS : 0 }));
  start.gates.filter(g => !read.tick || TICK_GATES.has(g)).forEach(gate); // other START gates are evaluated on the post-END row
  let storedTotal = start.stored_total;
  let apChange = { sources: start.ap_change_sources };
  let startExpiry = { removals: start.removals };
  let aps = start.reset.aps;
  let reaction: 0 | 1 = start.reset.reaction;
  if (!read.tick && aps === 0 && reaction === 0 && nextId !== actorId) gate('auto-end-loop'); // a single combatant returns at once in legacy (next === ended)

  const unresolved = read.tick ? gates.filter(g => !TICK_GATES.has(g)) : gates;
  const skippedReasons = new Set([...unresolved, ...start.gates.filter(g => !read.tick || !TICK_GATES.has(g))]);
  let tick: TurnTickResult | undefined;
  let droppedTickEvidence = '';
  let startStatusSources: NonNullable<EndTurnCommand['start_status_sources']> = [];
  if (read.tick) {
    const recorded = recordingRollSource(read.tick.roll);
    let drawnRows: TurnTickResult['rows'] = [];
    try {
      const deriveStart = (after: Row, afterEndRoster: Row[], successor?: { id: string; wrap: boolean }): StartOfTurnResult => {
        const startId = successor?.id ?? nextId;
        const block = window ? windowStart() : acceptedStart(after, resolveStartOfTurn(after, { ...read, roster: () => afterEndRoster }, { refreshPool: !death, removedBefore: removedAtEnd, clockAdvanceSeconds: (successor?.wrap ?? wrap) ? ROUND_SECONDS : 0 }));
        gates.filter(g => !TICK_GATES.has(g)).forEach(g => skippedReasons.add(g));
        block.gates.filter(g => !TICK_GATES.has(g)).forEach(g => skippedReasons.add(g));
        if (block.reset.aps === 0 && block.reset.reaction === 0 && nextId !== actorId) skippedReasons.add('auto-end-loop');
        // A newly granted row requiring START expiry has no stored status identity yet.
        if (block.removals.some(r => r.status_id.startsWith('TICK-PENDING-'))) throw new HandBack('turn-tick:pending-start-expiry');
        const ids = new Set(block.ap_change_sources.filter(s => s.kind === 'status_added').map(s => s.id));
        startStatusSources = statusRows(after, 'target').filter(s => s.tick_added && ids.has(statusItemId(s))).map(s => {
          const source = read.startSource?.(s, ending);
          if (read.startSource && !source) throw new HandBack('turn-tick:start-source-not-bound');
          return { status_item_id: statusItemId(s), target: startId, caster: upper(s.status_caster_id), status_connected_id: upper(s.status_connected_id),
            ...(source ? { source } : {}) };
        });
        return block;
      };
      tick = resolveEndTurnTicks({ encounter, roster, endingId: death || legendary ? null : actorId, nextId, nextReaction: reaction, endRemoved: removedAtEnd,
        ...(!window && !legendary ? { nextAfterEnd: (after: Row[]) => {
          const alive = new Set(after.filter(row => !int(ecLean(row, 'encounter_creature_dead')) && !int(ecLean(row, 'encounter_creature_deleted'))).map(idOf));
          if (alive.has(nextId)) return { id: nextId, wrap };
          const ring = endedKey < 0 ? order : [...order.slice(endedKey + 1), ...order.slice(0, endedKey + 1)];
          const id = ring.find(candidate => alive.has(candidate));
          if (!id) throw new HandBack('turn-tick:no-living-successor');
          return { id, wrap: endedKey >= 0 && order.indexOf(id) <= endedKey };
        }, entriesFor: read.abilityEntries } : {}),
        skipStart: window, resumedMain: legendary && !window, endingEntries: read.abilityEntries(ending),
        startRemoved: new Set(startExpiry.removals.map(r => r.status_id)), entries: read.abilityEntries(next), read: { ...read.tick, roll: recorded }, deriveStart,
        endExpiries: endExpiry.removals, zones: zonePlan, prepareEnd: result => {
          // An END effect that changes a boss's eligibility cannot share this
          // snapshotted pointer decision. Skip that due effect before real draws.
          if (Object.entries(result.ec).some(([id, columns]) => order.includes(id) && id !== actorId
            && itemType(read.rowFor(id)!) === 'CREATURE' && int(read.value(read.rowFor(id)!, 'legendary_creature'))
            && ['aps', 'encounter_creature_dead', 'encounter_creature_private', 'controller_encounter_creature_id'].some(key => key in columns)))
            throw new HandBack('legendary-boundary:eligibility-changing-end-effect');
        }, prepare: result => {
          if (recorded.draws.length) drawnRows = JSON.parse(JSON.stringify(result.rows)) as TurnTickResult['rows'];
          if (result.rows.length + skippedReasons.size > 64) throw new HandBack('turn-tick:commit-capacity');
          read.prepareTick?.(result);
        } });
      if (tick.successor) {
        nextId = tick.successor.id; wrap = tick.successor.wrap;
        next = read.rowFor(nextId)!; nextEc = ecOf(next);
        delete tick.successor;
      }
      start = tick.start ?? start;
      storedTotal = start.stored_total; apChange = { sources: start.ap_change_sources }; startExpiry = { removals: start.removals };
      aps = start.reset.aps; reaction = start.reset.reaction;
      delete tick.start;
    } catch (error) {
      // Consent remains a pre-draw prompt. An unavailable tick save is omitted;
      // the plain boundary still has a valid independent save shape.
      if (!recorded.draws.length && error instanceof HandBack && /trigger-consent/.test(error.reason))
        return no(error.reason, [...gates, error.reason]);
      if (recorded.draws.length) droppedTickEvidence = ' drawn tick evidence=' + JSON.stringify({ rows: drawnRows, draws: recorded.draws });
      startStatusSources = [];
      gates.forEach(g => skippedReasons.add(g));
      start.gates.forEach(g => skippedReasons.add(g));
      if (start.reset.aps === 0 && start.reset.reaction === 0 && nextId !== actorId) skippedReasons.add('auto-end-loop');
      skippedReasons.add(error instanceof HandBack ? error.reason : 'turn-tick:exception');
    }
  }
  // ---- outcome (deterministic; every gate above read state only) ----
  const removals = [...endExpiry.removals, ...startExpiry.removals.filter(r => !removedAtEnd.has(r.status_id))];
  const byLoser = new Map<string, StatusRemoval[]>();
  for (const r of removals) if (read.rowFor(r.target_id)) byLoser.set(r.target_id, [...(byLoser.get(r.target_id) ?? []), r]);
  // Every held projection a removal touches: the loser's target bucket AND the caster's caster bucket (Codex A review F6).
  const byCaster = new Map<string, StatusRemoval[]>();
  for (const r of removals) if (read.rowFor(r.target_id) && r.caster_id && r.caster_id !== r.target_id) byCaster.set(r.caster_id, [...(byCaster.get(r.caster_id) ?? []), r]);
  const endingFrame = statusRemovalFrame(ending, byLoser.get(actorId) ?? [], byCaster.get(actorId) ?? []);
  const nextFrame = nextId === actorId ? { status_remove_ids: [], skills: {} } : statusRemovalFrame(next, byLoser.get(nextId) ?? [], byCaster.get(nextId) ?? []);
  const otherIds = [...new Set([...byLoser.keys(), ...byCaster.keys()])].filter(id => id !== actorId && id !== nextId);
  const otherFrames: Array<{ actor_id: string } & EndTurnRowFrame> = [];
  const others: Row[] = [];
  for (const id of otherIds) {
    const row = read.rowFor(id);
    if (!row) { skippedReasons.add('third-party-row-missing'); continue; }
    const f = statusRemovalFrame(row, byLoser.get(id) ?? [], byCaster.get(id) ?? []);
    otherFrames.push({ actor_id: id, ...f }); others.push(row);
  }
  const skipDetail = { boundary: 'end-turn', actorId, nextId };
  const reasons = [...skippedReasons];
  // A large missing-definition roster still advances; retain every reason in a
  // single GM message when individual receipts would exceed the save capacity.
  const skipped = (tick?.rows.length ?? 0) + reasons.length > 64
    ? [skipUnported(reasons.join('; ') + droppedTickEvidence, skipDetail)]
    : reasons.map((reason, i) => skipUnported(reason + (i === reasons.length - 1 ? droppedTickEvidence : ''), skipDetail));
  for (const row of skipped) row.roll_by = actorId;
  if (skipped.length) {
    tick ??= { rows: [], ec: {}, statusesAdded: [], statusesRemoved: [], statusesChanged: [], charges: [] };
    tick.rows.push(...skipped);
  }
  /** Every removal that LIVES on a row: its loser entries plus the caster projections it holds (the same ids the frame carries). */
  const onRow = (id: string): StatusRemoval[] => { const seen = new Set<string>(); return [...(byLoser.get(id) ?? []), ...(byCaster.get(id) ?? [])].filter(r => !seen.has(r.status_id) && seen.add(r.status_id)); };
  const apsUsed = Math.max(0, storedTotal - aps);
  const round = int(encounterField(encounter, 'current_round'));
  const frame: EndTurnFrame = {
    ...(skipped.length ? { skipped } : {}),
    ending: endingFrame,
    next: { ...nextFrame, ...(window ? { refresh: false } : storedTotal !== int(ecLean(next, 'aps_total')) ? { aps_total: storedTotal } : {}),
      aps, aps_used: apsUsed > 0 ? apsUsed : null, weapon_attack: 0, head_attack: 0, reaction, movement_pct: 0 },
    others: otherFrames,
    encounter: { current_encounter_creature_id_turn: nextId, current_round: wrap ? round + 1 : round, turn_phase: 'main',
      ...interlude },
    clock_seconds: wrap ? ROUND_SECONDS : 0,
  };
  const command: EndTurnCommand = {
    schema: END_TURN_SCHEMA, operation_id: read.uuid(), encounter_id: upper(input.encounterId), actor_id: actorId,
    expected_rev: int(ecOf(ending).movement_rev), rules_version: END_TURN_RULES_VERSION, next_id: nextId, next_expected_rev: int(nextEc.movement_rev),
    next_orb_queue_before: orbQueueText(nextEc.orb_queue), ending_orb_queue_before: orbQueueText(ecOf(ending).orb_queue), wrap,
    zone_dedupe_claims: zonePlan?.claims ?? [],
    roll_private: [0, 1, 2].includes(Number(input.rollPrivate)) ? Number(input.rollPrivate) : 0,
    before: { round, ...(legendary || window ? { turn_phase: String(encounterField(encounter, 'turn_phase') ?? 'main') } : {}), pending_main_turn_encounter_creature_id: String(encounterField(encounter, 'pending_main_turn_encounter_creature_id') ?? ''),
      legendary_turn_queue: String(encounterField(encounter, 'legendary_turn_queue') ?? ''),
      next: { aps_total: int(ecLean(next, 'aps_total')), encounter_creature_dead: int(ecLean(next, 'encounter_creature_dead')),
        aps: int(nextEc.aps), weapon_attack: int(nextEc.weapon_attack), head_attack: int(nextEc.head_attack), reaction: int(nextEc.reaction), movement_pct: Number(scalar(nextEc.movement_pct) ?? 0) } },
    writes: {
      ending: { status_remove: onRow(actorId) },
      next: { ...(window ? { refresh: false } : frame.next.aps_total !== undefined ? { aps_total: frame.next.aps_total } : {}),
        aps, weapon_attack: 0, head_attack: 0, reaction, movement_pct: 0, status_remove: nextId === actorId ? [] : onRow(nextId) },
      others: otherFrames.map(f => ({ actor_id: f.actor_id, status_remove: onRow(f.actor_id) })),
      encounter: { current_encounter_creature_id_turn: nextId, ...(wrap ? { current_round: round + 1 } : {}),
        ...interlude },
      ap_change_sources: apChange.sources, log: ['end_turn', window ? 'start_legendary_turn' : 'start_turn'],
    },
    frame,
    ...(zonesChecked ? { zones_checked: zonesChecked, zone_inputs: zoneHolders.flatMap(holder => statusRows(holder, 'target'))
      .filter(s => int(s.status_aoe_source) !== 0).map(statusInputSnapshot).sort((a, b) => String(a.status_id).localeCompare(String(b.status_id))) } : { zone_inputs: [] }),
    ...(tick ? { tick } : {}),
    start_status_sources: startStatusSources,
  };
  return { supported: true, command, ending, next, others };
}

const ENCOUNTER_SCALARS = ['current_encounter_creature_id_turn', 'current_round', 'turn_phase', 'pending_main_turn_encounter_creature_id', 'legendary_turn_queue'] as const;
function setEncounterScalars(encounter: Row, values: Record<string, unknown>): void {
  const bucket = (encounter.encounters ??= {}) as Row;
  for (const key of ENCOUNTER_SCALARS) {
    if (!(key in values)) continue;
    bucket[key] = values[key];
    if (key in encounter) encounter[key] = values[key]; // a legacy root-level twin of the bucket key, when the blob carries one
  }
}

/** Apply a frame to the rows and the encounter IN PLACE (optimistic or received). Row identity is preserved. */
export function applyEndTurnTruth(rows: { ending: Row | null; next: Row | null; others: Row[] }, encounter: Row | null, frame: EndTurnFrame,
  revs: { ending?: number; next?: number } = {}, definition?: (id: string) => Row | null): void {
  const applyRow = (row: Row | null, part: EndTurnRowFrame, rev?: number): void => {
    if (!row) return;
    applyStatusFrame(row, part, id => definition?.(id) ?? record(record(encounter?.catalog)?.[id]));
    if (Number.isInteger(rev) && rev! >= 0) ((row.encounter_creatures ??= {}) as Row).movement_rev = rev;
  };
  const sameRow = !!rows.next && rows.next === rows.ending;
  applyRow(rows.ending, frame.ending, revs.ending);
  if (rows.next) {
    // The reset applies even when the next actor IS the ending actor (a single combatant — Codex A review F5).
    applyRow(rows.next, sameRow ? { status_remove_ids: frame.next.status_remove_ids, skills: frame.next.skills } : frame.next, sameRow ? undefined : revs.next);
    const ec = (rows.next.encounter_creatures ??= {}) as Row;
    if (frame.next.refresh !== false) {
      if (frame.next.aps_total !== undefined) ec.aps_total = frame.next.aps_total;
      applyApsScalar(ec, frame.next.aps, frame.next.aps_used);
      for (const key of ['weapon_attack', 'head_attack'] as const) { if (frame.next[key] === 0) delete ec[key]; else ec[key] = frame.next[key]; }
      ec.reaction = frame.next.reaction;
      if (frame.next.movement_pct === 0) delete ec.movement_pct; else ec.movement_pct = frame.next.movement_pct;
      const combat = record(record(record(rows.next.computed)?.creature)?.combat);
      if (combat) { const extra = int(combat.extra_attack); if (0 - extra === 0) delete combat.hindered_attack_counter; else combat.hindered_attack_counter = 0 - extra; }
    }
  }
  for (const other of frame.others) applyRow(rows.others.find(r => idOf(r) === upper(other.actor_id)) ?? null, other);
  if (encounter) setEncounterScalars(encounter, frame.encounter);
}
